// booth-ui.js - 展台弹窗的六个 UI 按钮，给 three.js 用。
//
// 设计取向：按钮走 DOM 叠层，不用 Sprite。
// 文字永远是矢量清晰的（不会被贴图放大糊掉），可以响应悬停/点击，
// 可以键盘 Tab 操作，可以被屏幕阅读器读到，改样式只要改 CSS。
// 位置则跟着 three.js 相机走：把按钮锚定在 3D 空间某个点，
// 相机一动按钮就跟着动，视觉上仍然贴住场景。
//
// 最小用法：
//
//   import * as THREE from 'three';
//   import { createBoothUI } from './booth-ui.js';
//
//   const ui = createBoothUI({
//     container: document.getElementById('stage'),
//     camera,
//     anchor: new THREE.Vector3(0, 1.1, 0),
//     railOffset: 2.4,
//     onSelect: (id) => console.log('clicked', id),
//   });
//
//   function animate() {
//     requestAnimationFrame(animate);
//     renderer.render(scene, camera);
//     ui.update();
//   }

import * as THREE from 'three';

export const ICONS = {
  information:
    '<circle cx="12" cy="12" r="9"/><path d="M12 10.6v6"/><circle cx="12" cy="7.6" r="0.9" fill="currentColor" stroke="none"/>',
  manual:
    '<path d="M4.5 5.2c0-.9.7-1.6 1.6-1.6h4.4c1.2 0 2.2 1 2.2 2.2v13c0-.9-.7-1.6-1.6-1.6H6.1c-.9 0-1.6-.7-1.6-1.6z"/><path d="M19.5 5.2c0-.9-.7-1.6-1.6-1.6h-4.4c-1.2 0-2.2 1-2.2 2.2v13c0-.9.7-1.6 1.6-1.6h4.9c.9 0 1.6-.7 1.6-1.6z"/>',
  video: '<rect x="3" y="5" width="18" height="14" rx="4"/><path d="M10.4 9.35 14.9 12l-4.5 2.65z"/>',
  exploded:
    '<path d="m12 3.2 8.2 4.1-8.2 4.1-8.2-4.1z"/><path d="m4.4 12.2 7.6 3.8 7.6-3.8"/><path d="m4.4 16.4 7.6 3.8 7.6-3.8"/>',
  photos:
    '<rect x="3" y="5" width="18" height="14" rx="3.4"/><circle cx="8.6" cy="10.2" r="1.6"/><path d="m4.2 16.8 4.6-4.1 3.4 3 3-2.6 4.6 3.9"/>',
  siteshots:
    '<path d="M3.6 8.6c0-1 .8-1.8 1.8-1.8h1.9l1.2-1.9h6.9l1.2 1.9h1.9c1 0 1.8.8 1.8 1.8v8.6c0 1-.8 1.8-1.8 1.8H5.4c-1 0-1.8-.8-1.8-1.8z"/><circle cx="12" cy="12.6" r="3.5"/>',
  chevron: '<path d="m9.5 5.5 6.5 6.5-6.5 6.5"/>',
};

// 六个按钮的默认配置：前三个在左，后三个在右。
export const DEFAULT_BUTTONS = [
  { id: 'information', label: 'Information', icon: 'information' },
  { id: 'manual', label: 'Manual', icon: 'manual' },
  { id: 'video', label: 'Video', icon: 'video' },
  { id: 'exploded', label: 'Explosive', icon: 'exploded' },
  { id: 'photos', label: 'Photos', icon: 'photos' },
  { id: 'siteshots', label: 'Reference', icon: 'siteshots' },
];

const STYLE_ID = 'booth-ui-styles';

const CSS = [
  '.booth-ui { position: absolute; inset: 0; pointer-events: none; z-index: 5;',
  '  font-family: Inter, Bahnschrift, "Segoe UI", system-ui, sans-serif; }',
  '.booth-ui__rail { position: absolute; display: flex; flex-direction: column;',
  '  gap: var(--booth-gap, 26px); pointer-events: none; will-change: transform; }',
  '.booth-ui[data-layout="screen"] .booth-ui__rail { top: var(--booth-center-y, 48%); transform: translateY(-50%); }',
  '.booth-ui[data-layout="screen"] .booth-ui__rail[data-side="left"] { left: var(--booth-inset, 6.5%); }',
  '.booth-ui[data-layout="screen"] .booth-ui__rail[data-side="right"] { right: var(--booth-inset, 6.5%); }',
  '.booth-ui[data-layout="world"] .booth-ui__rail { transform: translate(-50%, -50%); }',
  '.booth-btn { pointer-events: auto; display: flex; align-items: center; gap: calc(var(--booth-btn-h, 72px) * 0.18);',
  '  width: var(--booth-btn-w, 238px); height: var(--booth-btn-h, 72px);',
  '  padding: 0 calc(var(--booth-btn-h, 72px) * 0.21) 0 calc(var(--booth-btn-h, 72px) * 0.26);',
  '  border-radius: var(--booth-radius, 16px); border: 1px solid rgba(255,255,255,0.2);',
  '  background: linear-gradient(160deg, rgba(255,255,255,0.15), rgba(255,255,255,0.04) 48%, rgba(255,255,255,0.012)), rgba(9,17,28,0.46);',
  '  backdrop-filter: blur(20px) saturate(150%); -webkit-backdrop-filter: blur(20px) saturate(150%);',
  '  box-shadow: 0 20px 44px rgba(5,12,22,0.30), inset 0 1px 0 rgba(255,255,255,0.26);',
  '  color: #eef5fa; cursor: pointer; text-align: left; appearance: none;',
  '  transition: transform 0.18s ease, border-color 0.18s ease, background 0.18s ease, box-shadow 0.18s ease; }',
  '.booth-btn:hover { border-color: color-mix(in srgb, var(--booth-accent, #8fdcec) 55%, transparent); }',
  '.booth-btn:active { transform: translateY(1px) scale(0.99); }',
  '.booth-btn:focus-visible { outline: 2px solid var(--booth-accent, #8fdcec); outline-offset: 3px; }',
  '.booth-btn[aria-pressed="true"] { border-color: var(--booth-accent, #8fdcec);',
  '  background: linear-gradient(160deg, rgba(143,220,236,0.22), rgba(143,220,236,0.06) 55%, rgba(255,255,255,0.02)), rgba(9,17,28,0.5);',
  '  box-shadow: 0 22px 48px rgba(5,12,22,0.34), inset 0 1px 0 rgba(255,255,255,0.30), 0 0 0 6px rgba(143,220,236,0.08); }',
  '.booth-btn__icon { display: flex; width: var(--booth-icon, 26px); height: var(--booth-icon, 26px); flex: 0 0 auto; color: var(--booth-accent, #8fdcec); }',
  '.booth-btn__icon svg { width: 100%; height: 100%; fill: none; stroke: currentColor; stroke-width: 1.6; stroke-linecap: round; stroke-linejoin: round; }',
  '.booth-btn__label { flex: 1 1 auto; min-width: 0; font-size: var(--booth-label-size, 17px); font-weight: 600;',
  '  letter-spacing: 0.05em; text-transform: uppercase; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }',
  '.booth-btn__label[data-long="true"] { font-size: var(--booth-label-size-long, 14.5px); letter-spacing: 0.01em; }',
  '.booth-btn__chev { display: flex; width: var(--booth-chev, 14px); height: var(--booth-chev, 14px); flex: 0 0 auto; color: rgba(238,245,250,0.42); }',
  '.booth-btn__chev svg { width: 100%; height: 100%; fill: none; stroke: currentColor; stroke-width: 1.8; stroke-linecap: round; stroke-linejoin: round; }',
  '.booth-ui[data-theme="solid"] .booth-btn { background: rgba(11,20,32,0.92); backdrop-filter: none; -webkit-backdrop-filter: none; }',
  '.booth-ui[data-theme="outline"] .booth-btn { background: rgba(9,17,28,0.18); backdrop-filter: blur(6px); -webkit-backdrop-filter: blur(6px); }',
  '@media (prefers-reduced-motion: reduce) { .booth-btn { transition: none; } }',
  // 手机 / 平板：窄按钮上标签不再省略号截断，必要时换行显示完整
  '@media (max-width: 1024px), (pointer: coarse) {',
  '  .booth-btn__label { white-space: normal; overflow: visible; text-overflow: clip;',
  '    line-height: 1.15; word-break: keep-all; } }',
].join('\n');

function ensureStyles() {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = CSS;
  document.head.appendChild(style);
}

export function createBoothUI(options = {}) {
  let {
    container = document.body,
    camera,
    anchor = null,
    railOffset = 2.4,
    layout = 'screen',
    inset = 0.065,
    centerY = 0.48,
    buttons = DEFAULT_BUTTONS,
    onSelect = () => {},
    activeId = null,
    theme = 'glass',
    accent = '#8fdcec',
    verticalOffset = 0,
  } = options;

  if (!camera) throw new Error('createBoothUI: 需要传入 camera');

  ensureStyles();

  const root = document.createElement('div');
  root.className = 'booth-ui';
  root.dataset.layout = layout;
  root.dataset.theme = theme;
  root.style.setProperty('--booth-accent', accent);
  root.style.setProperty('--booth-inset', typeof inset === 'number' ? inset * 100 + '%' : inset);
  root.style.setProperty('--booth-center-y', centerY * 100 + '%');
  if (options.gap) root.style.setProperty('--booth-gap', options.gap + 'px');
  if (options.buttonWidth) root.style.setProperty('--booth-btn-w', options.buttonWidth + 'px');
  if (options.buttonHeight) root.style.setProperty('--booth-btn-h', options.buttonHeight + 'px');
  if (options.radius) root.style.setProperty('--booth-radius', options.radius + 'px');

  const railBreak = options.railBreak != null ? options.railBreak : Math.ceil(buttons.length / 2);
  const nodes = new Map();
  let currentActive = activeId;
  let rails = { left: null, right: null };
  let visible = true;

  const setActive = (id) => {
    currentActive = id;
    nodes.forEach((el, key) => el.setAttribute('aria-pressed', String(key === id)));
  };

  const makeButton = (cfg) => {
    const el = document.createElement('button');
    el.type = 'button';
    el.className = 'booth-btn';
    el.dataset.id = cfg.id;
    el.setAttribute('aria-pressed', String(cfg.id === currentActive));
    if (cfg.title) el.title = cfg.title;

    const iconSvg = cfg.svg || ICONS[cfg.icon] || '';
    const iconSpan = iconSvg
      ? '<span class="booth-btn__icon"><svg viewBox="0 0 24 24" aria-hidden="true">' + iconSvg + '</svg></span>'
      : '';
    const labelSpan =
      '<span class="booth-btn__label"' +
      // 只有特别长的标签才预先把字号调小（INFORMATION 这类 11 个字符的，
      // 用和其他五个按钮一样大的字也放得下，统一交给自适应去判断）
      (cfg.label.length > 11 ? ' data-long="true"' : '') +
      '>' +
      cfg.label +
      '</span>';
    const chevSpan =
      '<span class="booth-btn__chev" aria-hidden="true"><svg viewBox="0 0 24 24">' +
      ICONS.chevron +
      '</svg></span>';
    el.innerHTML = iconSpan + labelSpan + chevSpan;

    el.addEventListener('click', () => {
      setActive(cfg.id);
      onSelect(cfg.id, cfg);
      root.dispatchEvent(new CustomEvent('booth:select', { detail: { id: cfg.id, button: cfg } }));
    });

    nodes.set(cfg.id, el);
    return el;
  };

  const buildRails = () => {
    if (rails.left) rails.left.remove();
    if (rails.right) rails.right.remove();
    nodes.clear();

    const leftRail = document.createElement('div');
    leftRail.className = 'booth-ui__rail';
    leftRail.dataset.side = 'left';
    const rightRail = document.createElement('div');
    rightRail.className = 'booth-ui__rail';
    rightRail.dataset.side = 'right';

    buttons.slice(0, railBreak).forEach((cfg) => leftRail.appendChild(makeButton(cfg)));
    buttons.slice(railBreak).forEach((cfg) => rightRail.appendChild(makeButton(cfg)));

    root.append(leftRail, rightRail);
    rails = { left: leftRail, right: rightRail };
  };

  const setButtons = (next) => {
    buttons = next.slice();
    buildRails();
    update();
  };

  const setVisible = (next) => {
    visible = next;
    root.style.display = next ? '' : 'none';
  };

  let lastRect = { width: 0, height: 0 };

  // 每帧调用：把锚点的世界坐标投影到屏幕上，更新两组按钮的位置。
  const update = () => {
    if (layout !== 'world' || !anchor) return;
    if (!visible) return;

    const rect = container.getBoundingClientRect();
    lastRect = rect;
    camera.updateMatrixWorld();

    const world = anchor.isVector3 ? anchor.clone() : anchor.getWorldPosition(new THREE.Vector3());

    const projected = world.clone().project(camera);
    if (projected.z > 1) {
      setVisible(false);
      return;
    }
    setVisible(true);

    const toScreen = (v) => ({
      x: (v.x * 0.5 + 0.5) * rect.width,
      y: (-v.y * 0.5 + 0.5) * rect.height,
    });

    const right = new THREE.Vector3().setFromMatrixColumn(camera.matrixWorld, 0).normalize();
    const leftPoint = toScreen(world.clone().addScaledVector(right, -railOffset).project(camera));
    const rightPoint = toScreen(world.clone().addScaledVector(right, railOffset).project(camera));
    const centerPoint = toScreen(projected);

    // 夹住位置：旋转到极端角度时按钮也不会跑出画面
    const margin = options.margin != null ? options.margin : 22;
    const railW = rails.left.offsetWidth || 238;
    const railH = rails.left.offsetHeight || 268;
    const minX = railW / 2 + margin;
    const maxX = rect.width - railW / 2 - margin;
    const minY = railH / 2 + margin;
    const maxY = rect.height - railH / 2 - margin;
    const clamp = (v, lo, hi) => Math.min(Math.max(v, lo), hi);

    const y = clamp(centerPoint.y + verticalOffset, minY, maxY);
    rails.left.style.left = clamp(leftPoint.x, minX, maxX) + 'px';
    rails.left.style.top = y + 'px';
    rails.right.style.left = clamp(rightPoint.x, minX, maxX) + 'px';
    rails.right.style.top = y + 'px';
  };

  const destroy = () => {
    root.remove();
    nodes.clear();
  };

  buildRails();
  container.appendChild(root);
  update();

  return {
    root,
    setActive,
    get activeId() {
      return currentActive;
    },
    setButtons,
    setVisible,
    update,
    destroy,
    get size() {
      return lastRect;
    },
  };
}

export default createBoothUI;
